/**
 * Keeping a child signed in for the whole evening.
 *
 * A session token lives fifteen minutes ([`tokens.ts`](../../server/src/tokens.ts))
 * and was never renewed, so the third lesson of an evening failed to load
 * ([D-131](../../../docs/DECISIONS.md#d-131)). This asks for a fresh one well
 * before the old runs out, and — when a tablet has been asleep past expiry —
 * signs in again with the device's remembered credential, which for a child
 * without a code means they never notice.
 *
 * A child *with* a code is sent back to their face on the picker instead. The
 * code is a sibling-squabble lock ([D-043](../../../docs/DECISIONS.md#d-043)),
 * and keeping it in memory to type it for them would make it no lock at all.
 */

import { type Client, type Session, troubleFrom } from './api.js';
import type { RememberedDevice } from './device.js';

/** Renew once fewer than this many seconds remain. Six minutes of a fifteen-minute token. */
export const RENEW_WITH_SECONDS_LEFT = 6 * 60;

/** Below this, renewal would race the expiry; sign in again instead. */
export const TOO_LATE_SECONDS = 20;

/**
 * Seconds until this token expires, read from its own `exp`.
 *
 * Decoding without verifying is fine here: this only decides *when to ask*. A
 * token that lies about its expiry is refused by the server all the same.
 */
export function secondsLeft(token: string, nowMs: number): number {
  try {
    const payload = token.split('.')[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp - Math.floor(nowMs / 1000) : 0;
  } catch {
    return 0;
  }
}

export type Next = 'wait' | 'renew' | 'sign-in-again';

export function whatNext(token: string, nowMs: number): Next {
  const left = secondsLeft(token, nowMs);
  if (left > RENEW_WITH_SECONDS_LEFT) return 'wait';
  return left > TOO_LATE_SECONDS ? 'renew' : 'sign-in-again';
}

/**
 * One step: renew if it is time, fall back to a fresh sign-in, and report.
 *
 * Returns the new session, `null` when the child must pick their face again,
 * or `'unchanged'` when nothing needed doing.
 */
export async function keepSignedIn(
  client: Client,
  session: Session,
  device: RememberedDevice | null,
  nowMs: number,
): Promise<Session | null | 'unchanged'> {
  const next = whatNext(session.token, nowMs);
  if (next === 'wait') return 'unchanged';

  if (next === 'renew') {
    try {
      const { token } = await client.auth.renew.mutate();
      return { ...session, token };
    } catch (error) {
      // A dropped connection is not a refusal. The token still has minutes on
      // it, so try again on the next check rather than signing a child out
      // because the Wi-Fi blinked.
      if (troubleFrom(error).kind === 'offline') return 'unchanged';
      // Refused — device revoked, four hours up. The fresh sign-in below says
      // which, because it re-checks everything from the credential.
    }
  }

  if (device === null || device.deviceId !== session.deviceId) return null;
  try {
    const { token } = await client.auth.start.mutate({
      deviceId: device.deviceId,
      credential: device.credential,
      learnerId: session.learnerId,
    });
    return { ...session, token };
  } catch (error) {
    // Offline: nothing can be done until it is back, and nothing is lost —
    // the outbox holds the answers. Keep the session and try again.
    if (troubleFrom(error).kind === 'offline') return 'unchanged';
    // A child with a code, or a device that has been revoked: back to the faces.
    return null;
  }
}
